<template>
  <div class="maps-view">
    <!-- Header de la vista -->
    <div class="maps-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon size="32" color="white" class="mr-3">mdi-map</v-icon>
          <div>
            <h1 class="maps-title">Mapa Interactivo MGI</h1>
            <p class="maps-subtitle">
              Sistema de Gestión de Incidencias - Visualización Geoespacial
            </p>
          </div>
        </div>

        <div class="header-right">
          <!--
            P13-V5A-R5 · RETIRED. A green "Sistema Operativo" chip was hard-coded: it was not
            bound to anything, so it asserted the system was healthy even while every backend was
            unreachable. No producer exists for a system-wide operational status on this view.
            The chip beside it is kept because it IS a measurement — `navigator.onLine` is read
            from the browser — and its label now says precisely what it measures: the browser's
            own connectivity, not the state of the backend.
          -->
          <v-chip
            :color="connectionStatus ? 'success' : 'error'"
            variant="outlined"
            :prepend-icon="connectionStatus ? 'mdi-wifi' : 'mdi-wifi-off'"
          >
            {{ connectionStatus ? 'Navegador en línea' : 'Navegador sin conexión' }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Navegación por tabs -->
    <div class="maps-navigation">
      <v-card flat>
        <v-tabs v-model="selectedView" bg-color="transparent" grow>
          <v-tab
            :value="0"
            @click="navigateToView('/maps')"
            prepend-icon="mdi-monitor-dashboard"
          >
            Monitoreo del Sistema
          </v-tab>
          <v-tab
            :value="1"
            @click="navigateToView('/maps/interactive')"
            prepend-icon="mdi-map-marker-multiple"
          >
            Vista Interactiva
          </v-tab>
        </v-tabs>
      </v-card>
    </div>

    <!-- Contenido principal -->
    <div class="maps-content">
      <!-- Contenedor principal del mapa -->
      <div class="map-main-container">
        <InteractiveMap />
      </div>

      <!-- Sidebar derecho con controles y estadísticas -->
      <div class="map-sidebar">
        <!-- Estadísticas rápidas -->
        <v-card flat class="ma-3">
          <v-card-title class="text-h6 pb-2">
            <v-icon class="mr-2">mdi-chart-line</v-icon>
            Resumen del Sistema
          </v-card-title>

          <!--
            P13-V5A-R5 · RETIRED. This panel published four business facts — "Incidencias
            Activas", "Supervisores", "Agencias" and "Rutas Activas" — seeded as the literals
            45 / 12 / 28 / 8. This module contains no ApiClient, axios or fetch call of any kind,
            so those figures never came from a read. Worse, they MOVED: a visible "Actualizar"
            button re-randomised all four through `Math.floor(Math.random() * n) + m` behind a
            2s fake delay, and a 30-second interval drifted the incident count by ±1, which made
            invented numbers look like a live feed on a routed super_admin / administration_manager
            page. The simulator, its interval and the button are removed with the figures.
            Real counts of what the map actually loaded are published by the map itself
            (MapStats, fed from useMapData over mapService); they are NOT withdrawn.
          -->
          <v-card-text data-testid="maps-system-summary-retired">
            <v-alert type="info" variant="tonal" density="compact">
              Estas cifras no tenían un productor canónico y fueron retiradas en P13-V5A-R5.
              Los conteos reales de incidencias, supervisores, agencias y rutas se muestran sobre
              el mapa y corresponden a los elementos efectivamente cargados desde el backend.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Monitor de sistema -->
        <v-card flat class="ma-3">
          <v-card-title class="text-h6 pb-2">
            <v-icon class="mr-2">mdi-heart-pulse</v-icon>
            Estado del Sistema
          </v-card-title>

          <!--
            P13-V5A-R5 · RETIRED. Three chips reported "Backend API: En línea", "Base de Datos:
            Conectada" and "Notificaciones: Activas". Every one was a literal in a `systemHealth`
            ref ('online' / 'connected' / 'active') that no code path ever wrote to — there is no
            health check, no probe and no endpoint behind them. The panel therefore reported the
            backend and the database as healthy unconditionally, including while they were down,
            which is the most dangerous form of the defect: a green light that cannot turn red.
          -->
          <v-card-text data-testid="maps-system-health-retired">
            <v-alert type="info" variant="tonal" density="compact">
              Este panel no tenía un productor canónico y fue retirado en P13-V5A-R5.
              El sistema no expone una verificación de estado del backend, de la base de datos ni
              del servicio de notificaciones para esta vista, por lo que no se puede afirmar que
              estén operativos.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Alertas del sistema -->
        <v-card flat class="ma-3">
          <v-card-title class="text-h6 pb-2">
            <v-icon class="mr-2">mdi-alert</v-icon>
            Alertas del Sistema
          </v-card-title>

          <!--
            P13-V5A-R5 · RETIRED. The alert list was a literal array of two invented alerts
            ("Carga Alta del Sistema" and "Actualización Disponible") whose timestamps were
            manufactured at module setup as `Date.now() - 300000` and `- 3600000` so they would
            always read as "5 minutes ago" and "1 hour ago" on every load. No alert producer feeds
            this view. Dismissing them only spliced the local array, so the panel could never show
            a real alert and could never stop showing the fake ones.
          -->
          <v-card-text data-testid="maps-system-alerts-retired">
            <v-alert type="info" variant="tonal" density="compact">
              Estas alertas no tenían un productor canónico y fueron retiradas en P13-V5A-R5.
              Esta vista no está suscrita a ninguna fuente de alertas del sistema.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Acciones rápidas -->
        <v-card flat class="ma-3">
          <v-card-title class="text-h6 pb-2">
            <v-icon class="mr-2">mdi-lightning-bolt</v-icon>
            Acciones Rápidas
          </v-card-title>

          <!--
            P13-V5A-R5 · RETIRED. Four buttons named capabilities this view does not have:
            "Nueva Incidencia", "Exportar Reporte" and "Configuración" each ran a `console.log`
            and a TODO comment — no navigation, no request, no export — while "Actualizar Datos"
            was the wired end of the randomiser retired above. A control that names a capability
            the product does not have is itself a false claim, so the controls are removed rather
            than left inert. The map's own refresh, which performs real reads, is unaffected.
          -->
          <v-card-text data-testid="maps-quick-actions-retired">
            <v-alert type="info" variant="tonal" density="compact">
              Estas acciones no estaban implementadas y fueron retiradas en P13-V5A-R5.
              La creación de incidencias, la exportación de reportes y la configuración se realizan
              desde sus propias secciones del sistema.
            </v-alert>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import InteractiveMap from './InteractiveMap.vue'

const router = useRouter()
const route = useRoute()

// Navegación
const selectedView = ref(0)

// Determinar vista actual basada en la ruta
const updateSelectedView = () => {
  if (route.path === '/maps/interactive') {
    selectedView.value = 1
  } else {
    selectedView.value = 0
  }
}

const navigateToView = (path: string) => {
  router.push(path)
}

// Conectividad del navegador: medición real provista por el propio navegador.
// No dice nada sobre el estado del backend.
const connectionStatus = ref(true)

const checkConnection = () => {
  connectionStatus.value = navigator.onLine
}

const handleOnline = () => {
  connectionStatus.value = true
}

const handleOffline = () => {
  connectionStatus.value = false
}

// Lifecycle
onMounted(() => {
  updateSelectedView()

  // Verificar conexión inicial
  checkConnection()

  // Escuchar cambios de conexión
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
})

onUnmounted(() => {
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
})

// Watch para cambios de ruta
watch(() => route.path, () => {
  updateSelectedView()
})
</script>

<style scoped>
/* Estilos del header */
.maps-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.maps-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 1rem 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 10;
}

.maps-navigation {
  background: white;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  z-index: 9;
  position: relative;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
}

.maps-title {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

.maps-subtitle {
  font-size: 0.95rem;
  opacity: 0.9;
  margin: 0.25rem 0 0 0;
  font-weight: 400;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Estilos del contenido principal */
.maps-content {
  flex: 1;
  display: flex;
  position: relative;
  overflow: hidden;
}

.map-main-container {
  flex: 1;
  position: relative;
  background: #f5f5f5;
}

.map-sidebar {
  width: 350px;
  background: white;
  border-left: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 1200px) {
  .maps-header {
    padding: 0.75rem 1rem;
  }

  .maps-title {
    font-size: 1.5rem;
  }

  .maps-subtitle {
    font-size: 0.85rem;
  }

  .map-sidebar {
    width: 300px;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .map-sidebar {
    display: none;
  }
}

/* Animaciones */
.v-card {
  transition: all 0.3s ease;
}

.v-chip {
  transition: all 0.2s ease;
}

/* Scrollbar personalizado para el sidebar */
.map-sidebar::-webkit-scrollbar {
  width: 6px;
}

.map-sidebar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.map-sidebar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.map-sidebar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
